import { ComponentIds, ValidationResultFail, } from "@inter-ikea-core/core";
import { ValidationBase } from "@inter-ikea-core/core-toolbox";
import { COMPONENT_IDS } from "../storage2ComponentIDs";
import { getEntityBlockConnectionHoleComponent, getEntityGuestConnections, getEntityHole, } from "../functions/blockConnectionHole";
import { entityIsSection } from "../functions/attachedTo";
import { getEntityHolesOccupied, getEntityProductRef, } from "../utils/entityUtils";
export class ConnectionHoleRestrictionValidation extends ValidationBase {
    constructor() {
        super(...arguments);
        this.name = "ConnectionHoleRestirctionValidation";
        this.id = "CONNECTION_HOLE_RESTRICTION_VALIDATION";
        /**
         * Checks if the current child and entity can possibly block each other
         * @param child
         * @param entity
         * @returns
         */
        this.isEligibleForConnectionHoleBlock = (child, entity) => child.hasComponent(COMPONENT_IDS.INSERT) &&
            child.id !== entity.id &&
            !this.entityAttachedToWall(child) &&
            this.checkGuestConnectorKey(child);
        /**
         * Checks if the host connector includes "insert-connector"
         * @param snapActionCandidateHostConnector
         * @returns
         */
        this.checkHostConnectorKey = (snapActionCandidateHostConnector) => snapActionCandidateHostConnector.key.includes("insert-connector");
        /**
         * Checks if the entity GuestConnectionsComponent includes "to-section"
         * @param entity
         * @returns
         */
        this.checkGuestConnectorKey = (entity) => {
            const entityGuestConnections = getEntityGuestConnections(entity);
            const entityGuestConnector = entityGuestConnections === null || entityGuestConnections === void 0 ? void 0 : entityGuestConnections.find((connector) => connector.guestConnector.key.includes("to-section"));
            if (!entityGuestConnector)
                return false;
            return true;
        };
        /**
         * Check if the entity is attached to the wall
         * @param entity
         * @returns
         */
        this.entityAttachedToWall = (entity) => {
            var _a;
            const connections = (_a = entity.getComponent(ComponentIds.GUEST_CONNECTIONS_COMPONENT_ID)) === null || _a === void 0 ? void 0 : _a.connections;
            if (!connections)
                throw new Error("Entity is missing GuestConnectionsComponent");
            const isAttachedToWall = connections.find((connection) => connection.hostConnector.key.includes("wall"));
            return !!isAttachedToWall;
        };
        /**
         * Get a list of Entities that are children to the current entity
         * @param host
         * @returns
         */
        this.getEntityChildren = (host) => {
            var _a;
            if (!entityIsSection(host))
                return null;
            const children = (_a = host.getComponent(ComponentIds.PARENT_COMPONENT_ID)) === null || _a === void 0 ? void 0 : _a.children;
            if (!children)
                throw new Error("Host is missing children");
            return children;
        };
        /**
         * Returns a list of all the connection holes that the dragged entity can't be placed in
         * @param entity
         * @param snapActionCandidateHole
         * @param entityBlockComponent
         * @returns
         */
        this.getSnapCandidateHolesToBlock = (entity, snapActionCandidateHole) => {
            // The minus 1 is needed to offset which connection holes are actaully covered by the entity
            const entityHolesOccupied = getEntityHolesOccupied(entity) - 1;
            const entityBlockComponent = getEntityBlockConnectionHoleComponent(entity);
            const snapActionCandidateHoleArray = [];
            if (entityBlockComponent) {
                snapActionCandidateHoleArray.push(
                // The connection hole that it is currently trying to attach to
                snapActionCandidateHole, 
                // The connection hole plus how many holes above the entity that should be blocked
                snapActionCandidateHole + entityBlockComponent.blockHoleAbove, 
                // The connection hole minus how many holes the entity occupies
                snapActionCandidateHole - entityHolesOccupied, 
                // The connection hole minus how many holes the entity occupies and minus how many extra holes below the entity that should be blocked
                snapActionCandidateHole -
                    entityHolesOccupied -
                    entityBlockComponent.blockHoleBelow);
            }
            else {
                snapActionCandidateHoleArray.push(snapActionCandidateHole, snapActionCandidateHole - entityHolesOccupied);
            }
            return snapActionCandidateHoleArray;
        };
        /**
         * Returns a list of all the connection holes that an insert will block
         * @param insert
         * @returns
         */
        this.getInsertHolesBlocked = (insert) => {
            const insertBlockComponent = getEntityBlockConnectionHoleComponent(insert);
            // If the insert has the blockComponent it can just return the list of holes
            if (insertBlockComponent) {
                return insertBlockComponent.blockedHoles;
            }
            else {
                // If the insert doesn't have the blockComponent we need to calculate which holes it should block so other inserts can interact correctly with it
                const insertGuestConnections = getEntityGuestConnections(insert);
                const insertHoleBlocked = getEntityHole(insertGuestConnections);
                const insertHolesOccupied = getEntityHolesOccupied(insert) - 1;
                return [insertHoleBlocked, insertHoleBlocked - insertHolesOccupied];
            }
        };
    }
    validateCurrentState(_entity) {
        return null;
    }
    /**
     * Validates if the current entity can be placed in certain connection holes
     * @param entity
     * @param snapActionCandidate
     * @returns
     */
    validateAvailableConnectionHoles(entity, snapActionCandidate) {
        const host = snapActionCandidate.host;
        if (!host && !this.entityAttachedToWall(entity))
            return null;
        const hostChildren = this.getEntityChildren(host);
        if (!hostChildren)
            return null;
        const snapActionCandidateHostConnector = snapActionCandidate.hostConnector;
        if (!this.checkHostConnectorKey(snapActionCandidateHostConnector))
            return null;
        const snapActionCandidateHolesToBlock = this.getSnapCandidateHolesToBlock(entity, snapActionCandidateHostConnector.hole);
        const snapActionCandidateCollideWithHole = hostChildren.find((child) => {
            if (this.isEligibleForConnectionHoleBlock(child, entity)) {
                const insertHoleBlocked = this.getInsertHolesBlocked(child);
                return insertHoleBlocked.some((hole) => snapActionCandidateHolesToBlock.includes(hole));
            }
        });
        return snapActionCandidateCollideWithHole
            ? new ValidationResultFail("Can not place product to close to each other", getEntityProductRef(entity))
            : null;
    }
    validateSnap(_snapActionCandidate) {
        const entity = _snapActionCandidate.guestRoot;
        if (!entity.hasComponent(COMPONENT_IDS.INSERT))
            return null;
        return this.validateAvailableConnectionHoles(entity, _snapActionCandidate);
    }
}
